<template>
  <div>
    <v-toolbar color="grey" dark>
      <v-toolbar-title>Label udskrift</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>

    <v-col cols="6" offset="3" class="mt-4">
      <v-card>
        <v-card-title>
          <v-card-title-text>
            <v-row>
              <v-col cols="auto" class="pt-8">
                <label>M{{ formattedSidsteNummer }}</label>
              </v-col>
              <v-col cols="auto">
                <v-text-field
                  v-model="sidsteNummer" 
                  label="Startnummer"
                  class="custom-input"
                  type="number"
                ></v-text-field>
                <v-text-field
                  v-model="newNumber"
                  label="New Number"
                  class="custom-input"
                  type="number"
                ></v-text-field>
              </v-col>
              <v-col cols="auto" class="pt-3">
                <v-btn color="success" class="mt-5 ml-16" @click="handlePrint">Udskriv</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" class="mt-5 ml-16" @click="updateNumbers">Opdater</v-btn>
              </v-col>
            </v-row>
          </v-card-title-text>
        </v-card-title>
      </v-card>
    </v-col>  
    <v-spacer></v-spacer>
  </div>
</template>


<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';


const store = useStore();
const numbers = ref([]);
const newNumber = ref(0);
const sidsteNummer = ref(0);

const labelWidth = 50;
const labelHeight = 24;

// Computed property to format the number with leading zeros for display
const formattedSidsteNummer = computed(() => {
    return sidsteNummer.value.toString().padStart(7, '0');
});

const formattedCurrentNummer = (number) => {
    if (number === undefined || number === null) {
        console.warn("Received undefined or null for number"); // Add a log for debugging
        return ''; // Return an empty string or a default formatted value
    }

    return number.toString().padStart(7, '0'); // Format the number with leading zeros
};

onMounted(async () => {
  await fetchNumbers();
});

watch(sidsteNummer, async (newValue) => {
    if (newValue) {
        const updatedNumber = parseInt(newValue, 10);
        console.log('Updating number from sidsteNummer:', updatedNumber);
        await store.dispatch('mortalin/updateNumbers', { number: updatedNumber, amount: newNumber.value });
    }
});

watch(newNumber, async (newValue) => {
    if (newValue) {
        const updatedAmount = parseInt(newValue, 10);
        console.log('Updating amount from newNumber:', updatedAmount);
        await store.dispatch('mortalin/updateNumbers', { number: sidsteNummer.value, amount: updatedAmount });
    }
});

const updateNumbers = async () => {
    try {
        const updatedNumber = sidsteNummer.value;
        const updatedAmount = newNumber.value;
        console.log('Updating numbers:', { number: updatedNumber, amount: updatedAmount });
        await store.dispatch('mortalin/updateNumbers', { number: updatedNumber, amount: updatedAmount });
    } catch (error) {
        console.error('Error updating numbers:', error);
    }
};

const fetchNumbers = async () => {
  try {
    await store.dispatch('mortalin/fetchLastNumber');
    numbers.value = store.state.mortalin.lastNumber.numbers;

    // Populate newNumber with the 'antal' from the fetched data
    if (numbers.value.length > 0) {
      newNumber.value = numbers.value[0].antal;
      sidsteNummer.value = numbers.value[0].nummer; // Set newNumber to the antal value
    }

    console.log('Fetched numbers:', numbers.value);
  } catch (error) {
    console.error('Error fetching numbers:', error);
  }
};

// Function to handle print logic based on the quantity
const handlePrint = () => {
    const antal = newNumber.value; // Get the number of labels
    // const rawNumber = numbers.value[0].nummer; // Make sure we have the proper integer format

    // Use formattedSidsteNummer.value for generating the label
    if (antal <= 1) {
        printLabel(formattedSidsteNummer.value, antal); // Call printLabel for single labeling
    } else {
        printLabels(); // Call printLabels for multiple labeling
    }
};

const printLabel = async (sidsteNummer, antal) => {
    const enkeltPDF = new jsPDF({
        unit: "mm",
        orientation: "l",
        format: [labelWidth, labelHeight]
    });

    const numLabels = antal || 1; // Use the quantity for the number of labels
    for (let i = 0; i < numLabels; i++) {
        if (i > 0) {
            enkeltPDF.addPage([labelWidth, labelHeight]); // Add new page for each additional label
        }
        
        // Generate the PDF content for the provided item using formattedSidsteNummer
        generatePDF(enkeltPDF, formattedSidsteNummer.value, labelWidth, labelHeight); // Use the formatted number with 'M'
    }

    // Create the PDF output
    enkeltPDF.output('dataurlnewwindow', `enkelt_label.pdf`);

    // Send a request to update the numbers
    console.log('Sending request to update numbers:', { 
        number: sidsteNummer, 
        amount: newNumber.value 
    });
    await store.dispatch('mortalin/updateNumbers', { 
        number: numbers.value[0].nummer, 
        amount: newNumber.value
    }); // Sending raw numbers
    await fetchNumbers(); // Refresh numbers from state to reflect any updates
};

const printLabels = async () => {
    const antal = newNumber.value; // Total quantity of labels to print
    const startNumber = parseInt(sidsteNummer.value.toString().replace('M', ''), 10); // Get raw number from formatted string

    if (isNaN(startNumber)) {
        console.error('Start number is NaN:', startNumber);
        return; // Safely exit if the start number is invalid
    }

    const batchSize = 100; // Define the batch size
    const totalBatches = Math.ceil(antal / batchSize); // Calculate total number of batches
    const mergedPDF = new jsPDF({
        orientation: "l",
        unit: "mm",
        format: [labelWidth, labelHeight]
    });

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const currentBatchSize = Math.min(batchSize, antal - batchIndex * batchSize); // Calculate size of current batch
        for (let i = 0; i < currentBatchSize; i++) {
            const currentNumber = startNumber + batchIndex * batchSize + i; // Increment the base number for each label
            const barcodeContent = formattedCurrentNummer(currentNumber); // Prepare formatted barcode for each label

            // Generate the PDF content for the current label
            generatePDF(mergedPDF, barcodeContent, labelWidth, labelHeight);

            // Only add a new page if not the last label in the batch
            if (i < currentBatchSize - 1 || batchIndex < totalBatches - 1) {
                mergedPDF.addPage();
            }
        }

        // Pause between batches (1 second in this example)
        // await delay(1000); 

        // Optionally: Uncomment the following line if you want to output the current batch to avoid large memory usage per batch
        // mergedPDF.output('dataurlnewwindow', `merged_label_batch_${batchIndex + 1}.pdf`);
    }

    // Create the final PDF output after all batches are processed
    // mergedPDF.output('dataurlnewwindow', `merged_label.pdf`);
    // Save the PDF instead of opening in a new window
    const pdfFileName = `merged_label.pdf`;
    mergedPDF.save(pdfFileName);

    // After printing, update the last number in the database
    const updatedNumber = parseInt(startNumber, 10) + parseInt(antal, 10); // This calculates the new total number correctly
    console.log('Numbers being updated:', updatedNumber, antal); // Log the updated values
    await store.dispatch('mortalin/updateNumbers', { number: updatedNumber, amount: antal }); // Update the number and amount

    // Fetch updated numbers to reflect the changes
    await fetchNumbers(); // Refresh numbers from state to reflect any updates

    // After saving the PDF
    // const pdfBlob = mergedPDF.output('blob'); // Get the PDF as a Blob
    // const pdfUrl = URL.createObjectURL(pdfBlob); // Create a URL for the Blob

    // Open the PDF in a new window/tab for printing
    // const printWindow = window.open(pdfUrl);
    //  printWindow.onload = () => {
    //      printWindow.print(); // Automatically open the print dialog
    //      printWindow.close(); // Close the window after printing
    //  };
};

// Helper function to create a delay
const delay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};


const generatePDF = (doc, labelNumber, labelWidth, labelHeight) => {
  const availableWidth = labelWidth - 2;


  // BARCODE C128
  let barcodeContent = `M${labelNumber}`;
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, barcodeContent, {
      format: 'CODE128',
      displayValue: false
  });
  let barcodeImageData = canvas.toDataURL('image/jpeg');

  if (barcodeImageData) {
    doc.addImage(barcodeImageData, 'JPEG', 3, -0.5, 44, 23);
  }
 
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(barcodeContent, 25, 23, {align: "center"})

};
</script>

<style>
.custom-input {
  background-color: #f0f0f0;
  color: #333;
}
</style>